<template>
  <v-card id="signature_participants" width="100%">
    <v-container fluid>
      <v-card-title>
        {{ signatureChain.status === "CREATED" ? $t('signatureChains.participants.hasTobeSignedBy') :
          $t('signatureChains.participants.haveBeenSignedBy') }}
      </v-card-title>
      <v-row class="grey--text">
        <v-card-subtitle class="d-flex flex-row align-baseline pb-0">
          <v-icon small class="mr-2">mdi-account-circle-outline</v-icon>
          <div v-if="createdBy">
            {{ $t('signatureChains.participants.createdBy') }} {{ createdBy.firstName }} {{ createdBy.lastName }} &lt;{{
              createdBy.email }}&gt;
          </div>
          <div v-else>
            {{ $t('signatureChains.participants.userDeletedText') }}
          </div>
        </v-card-subtitle>
      </v-row>
      <v-row id="chain_comment" class="grey--text">
        <v-card-subtitle v-if="signatureChain.comment" class="d-flex flex-row align-baseline pb-0">
          <v-icon small class="mr-2">mdi-comment</v-icon>
          {{ signatureChain.comment }}
        </v-card-subtitle>
      </v-row>
      <v-row>
        <v-col>
          <v-data-table
            id="participants_table" class="virtual-scroll-table" :items-per-page="-1" :headers="headers"
            :items="signatureChain.signatures" :disable-sort="!isUnordered" hide-default-footer dense
          >
            <template #item.rank="{ index }">
              <span>{{ !isUnordered ? `${index + 1}.` : '•' }}</span>
            </template>
            <template #item.user.name="{ item }">
              <div class="participant_name">
                <span v-if="item.status === 'DELETED'" id="participant_deleted" class="font-italic caption">
                  {{ $t('signatureChains.participants.userDeletedText') }}
                </span>

                <span v-else>
                  {{
                    getSignerDisplayedName(item)
                  }}
                </span>
              </div>
            </template>
            <template #item.user.email="{ item }">
              <span v-if="!!item.user" id="user_email" class="caption participant_email">
                &lt;<long-text :text="item.user.email" :max-char="29">
                </long-text>&gt;
              </span>
              <span v-else class="participant_email"></span>
            </template>
            <template #item.signedAt="{ item }">
              <div class="font-italic caption">
                <span v-if="!!item.signedAt">{{ convertDate(item.signedAt) }}</span>
                <span v-else>{{ $t('signatureChains.participants.notSignedYet') }}</span>
              </div>
            </template>
            <template #item.status="{ item }">
              <div class="d-flex flex-row align-center justify-start">
                <v-chip label class="text--darken-1" :class="getStatusStyle(item)">
                  {{ getStatusText(item) }}
                </v-chip>
                <div v-if="!!item.comment" id="tooltipContainer">
                  <v-tooltip right>
                    <template #activator="{ on, attrs }">
                      <v-icon class="ml-1" :color="(item.status === 'FAILED' || item.status === 'DECLINED') ? 'error' : 'success'" v-bind="attrs" v-on="on">
                        {{ item.status === 'FAILED' ? 'mdi-help-circle-outline': 'mdi-message-check' }}
                      </v-icon>
                    </template>
                    {{ item.comment }}
                  </v-tooltip>
                </div>
                <div v-if="item.clerkMode" id="tooltipContainer">
                  <v-tooltip right>
                    <template #activator="{ on, attrs }">
                      <v-icon :color="getSubsignerIconColor(item)" class="ml-1" v-bind="attrs" v-on="on">
                        mdi-information-outline
                      </v-icon>
                    </template>

                    <span v-if="!!item.subsignerName">{{ getSubsignerTooltipText(item) }}</span>
                    <span v-else>{{ $t('signatureChains.participants.clerk.noSubsigner') }} </span>
                  </v-tooltip>
                </div>
              </div>
            </template>
            <template #item.copyLink="{ item }">
              <v-btn v-if="item.status === 'CREATED'" id="copy_signing_url_button" class="pa-1 copyURL" text @click="copyItemSigningURL(item)">
                <v-icon>
                  mdi-content-copy
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>

          <!-- VIEWERS -->

          <v-card-title>
            {{ $t('signatureChains.participants.viewers.title') }}
          </v-card-title>
          <v-data-table
            id="viewer_table" class="virtual-scroll-table mb-2" :items-per-page="-1"
            :headers="viewerHeaders" :items="addedUsers" hide-default-footer
            :no-data-text="$t('signatureChains.participants.viewers.noViewerYet')"
          >
            <template #item.name="{ item }">
              <div>
                <span v-if="!!item.firstName || !!item.lastName">{{ item.firstName }} {{ item.lastName }}</span>
                <span v-else class="font-italic caption">
                  {{ item.status === 'DELETED' ? $t('signatureChains.participants.userDeletedText') :
                    $t('signatureChains.participants.invitedUser') }}
                </span>
              </div>
            </template>
            <template #item.email="{ item }">
              <span class="caption">
                &lt;<long-text :text="item.email" :max-char="29">
                </long-text>&gt;
              </span>
            </template>
            <template v-if="isOwner" #item.actions="{ item }">
              <div>
                <v-btn
                  id="remove_viewer_button" outlined color="error" class="pl-1"
                  @click="removeViewer(item, $event)"
                >
                  <v-icon class="ml-0 mr-1">mdi-eye-off-outline</v-icon>
                  {{ $t('signatureChains.participants.viewers.remove') }}
                </v-btn>
              </div>
            </template>
          </v-data-table>
          <v-autocomplete
            v-if="isOwner" id="add_viewer_autocomplete" v-model="nextUserToAdd" :items="enabledUsers"
            item-text="email" return-object :label="$t('signatureChains.participants.viewers.addViewer')"
            :placeholder="$t('autocomplete.nameOrEmail')" prepend-inner-icon="mdi-fountain-pen-tip"
            :no-data-text="noDatatext" clearable outlined hide-details dense :search-input="search"
            @update:search-input="listEnabledUsersWacther" @keydown.enter="addUser" @click.prevent="addUser"
          >
            <template slot="item" slot-scope="{ item }">
              <v-row align-content="center" @mouseup="addUser(item)">
                <span class="body-2 py-4">{{ item.firstName }} {{ item.lastName }} <span
                  v-if="!!item.email"
                  class="caption"
                >&lt;{{ item.email }}&gt; </span></span>
                <v-spacer></v-spacer>
              </v-row>
            </template>
            <!-- Button to add user if no results found -->
            <template v-if="!userGroupsEnabled && !enabledUsers.length && displayInviteButton" #no-data>
              <v-row>
                <v-col class="ml-3">
                  <v-label class="ml-6">{{ noDatatext }}</v-label>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-btn class="ml-3" color="primary" @click="addUserAsInvite()">
                    {{
                      $t('documentSignatureCustomization.content.createChain.iniviteUserButton') }}
                  </v-btn>
                </v-col>
              </v-row>
            </template>
          </v-autocomplete>

          <v-btn id="cancel_button" class="mx-2 mt-5" @click="$emit('close')">
            <v-icon class="mr-1">mdi-close-circle-outline</v-icon>
            {{ $t("main.cancel") }}
          </v-btn>
          <v-btn
            v-if="showSaveButton" id="save_viewers_button" class="mx-2 mt-5" color="success"
            :loading="isSaveButtonLoading" @click="saveViewers"
          >
            <v-icon class="mr-1">mdi-checkbox-marked-circle-outline</v-icon>
            {{ $t('main.save') }}
          </v-btn>
          <v-btn
            v-if="resendInvitationsVisible" id="resend_button" class="mx-2 mt-5" :loading="isResendButtonLoading"
            @click="resendInvitations"
          >
            <v-icon class="mr-1">mdi-send-outline</v-icon>
            {{ $t('signatureChains.participants.resend') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import LongText from '@/components/LongText.vue'
import seal from '@/services/api/seal.js'
import users from '@/services/api/users'
import debounce from 'lodash.debounce'
import { showConfirmationMenu, showToastError, showToastSuccess } from '../../../event-bus.js'
import signatureChains from '../../../services/api/signatureChains.js'
import signatureChainListStatus from './SignatureChainListStatus.js'
import SignatureChainStatus from './SignatureChainStatus'
import SignatureChainType from './SignatureChainType.js'
import memberRole from './memberRole.js'

export default {
  name: 'SignatureChainDetails',

  components: {
    LongText
  },
  props: {
    signatureChain: {
      type: Object,
      default: undefined
    }
  },
  emits: ['close'],

  data() {
    return {
      headers: [
        {
          text: this.$t('dataTable.header.users.rank'),
          sortable: false,
          align: 'left',
          value: 'rank',
          width: '1%'
        },
        {
          text: this.$t('dataTable.header.users.name'),
          align: 'start',
          value: 'user.name'
        },
        {
          text: this.$t('dataTable.header.users.email'),
          align: 'start',
          value: 'user.email'
        },
        {
          text: this.$t('dataTable.header.signatureChains.signedAt'),
          align: 'start',
          value: 'signedAt'
        },
        {
          text: this.$t('dataTable.header.users.status'),
          align: 'start',
          value: 'status'
        },
        {
          text: this.$t('dataTable.header.users.copyLink'),
          align: 'end',
          value: 'copyLink'
        }
      ],
      // vierwers adding thing
      enabledUsers: [],
      addedUsers: this.signatureChain.viewers,
      noDatatext: this.$t('autocomplete.startTyping'),
      memberRole: memberRole,
      search: '',
      nextUserToAdd: undefined,
      showSaveButton: false,
      isSaveButtonLoading: false,
      isResendButtonLoading: false,
      displayInviteButton: false,
      invitableUserEmail: '',
      userGroupsEnabled: this.$store.state.remoteConfig ? this.$store.state.remoteConfig.userGroupsEnabled : false,
      sealStatus: {
        enabled: false,
        secureMode: false,
        activated: false
      }
    }
  },
  computed: {
    isUnordered() {
      return this.signatureChain.type === SignatureChainType.UNORDERED
    },

    currentUser() {
      return this.$store.state.currentUser
    },

    createdBy() {
      return this.signatureChain.createdBy
    },

    isOwner() {
      return !!this.createdBy && this.currentUser.id === this.createdBy.id
    },

    viewerHeaders() {
      if (this.isOwner) {
        return [
          {
            text: this.$t('dataTable.header.users.name'),
            align: 'start',
            value: 'name'
          },
          {
            text: this.$t('dataTable.header.users.email'),
            align: 'center',
            value: 'email'
          },
          {
            text: this.$t('dataTable.header.actions'),
            align: 'end',
            value: 'actions',
            sortable: false
          }]
      }

      return [
        {
          text: this.$t('dataTable.header.users.name'),
          align: 'start',
          value: 'name'
        },
        {
          text: this.$t('dataTable.header.users.email'),
          align: 'center',
          value: 'email'
        }
      ]
    },
    resendInvitationsVisible() {
      const hasInvitedSigner = this.signatureChain.signatures.find(signature => !(signature.user?.lastName && signature.user?.firstName))

      const hasInvitedViewever = this.signatureChain.viewers.find(viewer => !(viewer.firstName && viewer.lastName))
      return !this.userGroupsEnabled && (hasInvitedSigner || hasInvitedViewever)
    }

  },
  async mounted() {
    this.sealStatus = await seal.getSealStatus()
  },

  methods: {
    currentUserRank() {
      return this.signatureChain.signatures.findIndex(x => x.user.id === this.currentUser.id)
    },

    getStatusStyle(signature) {
      let indexOfCurrentSigId = -1
      if (this.signatureChain.currentSignatureId) {
        indexOfCurrentSigId = this.signatureChain.signatures.findIndex(x => x.id === this.signatureChain.currentSignatureId)
      }

      if (signature.status === signatureChainListStatus.FAILED.SUSPENDED) {
        return 'error lighten-3 error--text suspended-chip'
      }

      if (signature.status === 'DONE') {
        return 'signed signed-chip'
      } else if (signature.status === signatureChainListStatus.FAILED.DELETED || signature.status === signatureChainListStatus.FAILED.SUSPENDED || signature.status === signatureChainListStatus.FAILED.DECLINED || signature.status === signatureChainListStatus.FAILED.WITHDRAWN) {
        return `error lighten-3 error--text ${signature.status.toLowerCase()}-chip`
      } else if (this.isUnordered || this.signatureChain.signatures[0].id === signature.id || signature.id === this.signatureChain.signatures[indexOfCurrentSigId + 1].id) {
        return 'todo todo-chip'
      } else {
        return 'waiting-chip grey lighten-2'
      }
    },
    getSubsignerIconColor(signature) {
      let indexOfCurrentSigId = -1
      if (this.signatureChain.currentSignatureId) {
        indexOfCurrentSigId = this.signatureChain.signatures.findIndex(x => x.id === this.signatureChain.currentSignatureId)
      }

      if (signature.status === signatureChainListStatus.FAILED.SUSPENDED) {
        return 'error'
      }

      if (signature.status === 'DONE') {
        return 'success'
      } else if (signature.status === signatureChainListStatus.FAILED.DELETED || signature.status === signatureChainListStatus.FAILED.SUSPENDED || signature.status === signatureChainListStatus.FAILED.DECLINED || signature.status === signatureChainListStatus.FAILED.WITHDRAWN) {
        return 'error'
      } else if (this.isUnordered || this.signatureChain.signatures[0].id === signature.id || signature.id === this.signatureChain.signatures[indexOfCurrentSigId + 1].id) {
        return 'primary'
      } else {
        return 'grey'
      }
    },
    getSubsignerTooltipText(signature) {
      const name = signature.subsignerName
      let indexOfCurrentSigId = -1
      if (this.signatureChain.currentSignatureId) {
        indexOfCurrentSigId = this.signatureChain.signatures.findIndex(x => x.id === this.signatureChain.currentSignatureId)
      }

      if (signature.status === signatureChainListStatus.FAILED.SUSPENDED) {
        return this.$t('signatureChains.participants.clerk.subsigner.failed', { 0: name })
      }

      if (signature.status === 'DONE') {
        return this.$t('signatureChains.participants.clerk.subsigner.signed', { 0: name })
      } else if (signature.status === signatureChainListStatus.FAILED.DELETED || signature.status === signatureChainListStatus.FAILED.SUSPENDED || signature.status === signatureChainListStatus.FAILED.DECLINED || signature.status === signatureChainListStatus.FAILED.WITHDRAWN) {
        return this.$t('signatureChains.participants.clerk.subsigner.failed', { 0: name })
      } else if (this.isUnordered || this.signatureChain.signatures[0].id === signature.id || signature.id === this.signatureChain.signatures[indexOfCurrentSigId + 1].id) {
        return this.$t('signatureChains.participants.clerk.subsigner.todo', { 0: name })
      } else {
        return ''
      }
    },
    getSignerDisplayedName(item) {
      if (!item.user) {
        return this.$t('signatureChains.participants.userDeletedText')
      }

      if ((!item.user.firstName && !item.user.lastName)) {
        return this.$t('signatureChains.participants.unknownUser')
      }

      return `${item.user.firstName || ''} ${item.user.lastName || ''}`.trim()
    },
    getStatusText(signature) {
      let indexOfCurrentSigId = -1
      if (this.signatureChain.currentSignatureId) {
        indexOfCurrentSigId = this.signatureChain.signatures.findIndex(
          (x) => x.id === this.signatureChain.currentSignatureId
        )
      }
      if (signature.approver) {
        if (signature.status === signatureChainListStatus.FAILED.FAILED) {
          return this.$t('signatureChains.states.failedTypes.failed')
        } else
        if (signature.status === signatureChainListStatus.FAILED.DECLINED) {
          return this.$t('signatureChains.states.failedTypes.declined')
        } else if (signature.status === 'CREATED') {
          if (signature.user.id === this.currentUser.id) {
            return this.$t('signatureChains.states.todo')
          }
          return this.$t('signatureChains.states.pending')
        } else {
          return this.$t('signatureChains.states.approved')
        }
      } else {
        switch (signature.status) {
          case signatureChainListStatus.FAILED.FAILED:
            return this.$t('signatureChains.states.failedTypes.failed')
          case signatureChainListStatus.FAILED.SUSPENDED:
            return this.$t('signatureChains.states.failedTypes.suspended')
          case signatureChainListStatus.FAILED.DELETED:
            return this.$t('signatureChains.states.failedTypes.deleted')
          case 'DONE':
            return this.$t('signatureChains.participants.alreadySigned')
          case signatureChainListStatus.FAILED.DECLINED:
            return this.$t('signatureChains.states.failedTypes.declined')
          case signatureChainListStatus.FAILED.WITHDRAWN:
            return this.$t('signatureChains.states.failedTypes.withdrawn')

          default:
            if (this.isUnordered || this.signatureChain.signatures[0].id === signature.id || signature.id === this.signatureChain.signatures[indexOfCurrentSigId + 1].id) {
              return this.$t('signatureChains.states.todo')
            }

            return this.$t('signatureChains.states.waiting')
        }
      }
    },

    async removeViewer(user, e) {
      const opts = {
        title: this.$t('dialog.confirmation.title'),
        posX: e.clientX,
        posY: e.clientY
      }

      const conf = await showConfirmationMenu(opts)
      if (!conf) {
        return
      }

      const newViewers = this.addedUsers.filter(x => x.id !== user.id)
      this.addedUsers = newViewers
      this.showSaveButton = true
    },
    listEnabledUsersWacther: debounce(function(search) {
      this.listEnabledUsers(search)
    }, 500),
    isValidEmail(email) {
      // Regular expression for validating an email address
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    async listEnabledUsers(searchUser) {
      const pageReq = {
        maxResults: 5
      }
      let searchUserlen = 0
      try {
        searchUserlen = searchUser.length
      } catch (err) {

      }
      const signers = this.signatureChain.signatures.map(x => x.user)

      if (searchUserlen > 0) {
        try {
          const resp = await users.listEnabledUsers(searchUser, pageReq)

          this.enabledUsers = resp.items.filter(user => !(this.addedUsers.some(addedUser => addedUser.id === user.id) || signers.some(signer => signer.id === user.id)))

          if (resp.items.length === 0) {
            this.noDatatext = this.$t('documentSignatureCustomization.content.createChain.userNotFound')

            this.displayInviteButton = false
          } else {
            this.displayInviteButton = false
          }
        } catch (error) {
          const errorMsg = this.getErrorMessage(error)
          showToastError(errorMsg)
        }
      } else {
        this.noDatatext = this.$t('autocomplete.startTyping')
      }
    },
    async addUser(user) {
      if (user.id === this.currentUser.id || this.addedUsers.find(v => v.id === user.id)) {
        this.search = ''
        return
        // user.role = memberRole.CREATOR_AND_SIGNER // orig
      } else {
        user.role = memberRole.VIEWER
      }

      this.nextUserToAdd = user

      // we need to copy the object and assign a unique id for it to avoud Vue duplicate key warnings
      this.nextUserToAdd = Object.assign({}, this.nextUserToAdd)
      this.nextUserToAdd.uniqueId = this.addedUsers.length + 1
      // explantaion: somehow a empty array is not an array: error: .include is  not a function
      if ((this.nextUserToAdd !== undefined) && (this.nextUserToAdd.email)) {
        this.addedUsers.push(this.nextUserToAdd)
        // this.$set(this.addedUsers, this.addedUsers.length - 1, this.nextUserToAdd)
        if (this.nextUserToAdd.id === this.currentUser.id) {
          this.includesHimself = true
        }
        this.nextUserToAdd = ''
        this.search = ''
        this.displayInviteButton = false
        this.showSaveButton = true
      }
    },
    addUserAsInvite() {
      const invitableUser = {}
      invitableUser.role = memberRole.SIGNER
      invitableUser.email = this.invitableUserEmail
      this.addUser(invitableUser)
    },
    async saveViewers() {
      try {
        this.isSaveButtonLoading = true
        await signatureChains.updateSignatureChain(this.signatureChain.id, this.addedUsers.map(x => x.email))
        this.isSaveButtonLoading = false
        this.showSaveButton = false
      } catch (error) {
        console.error(error)
        showToastError('Error')
      } finally {
        this.isSaveButtonLoading = false
      }
    },
    async resendInvitations() {
      try {
        this.isResendButtonLoading = true
        await signatureChains.resendInvitations(this.signatureChain.id)
        this.isResendButtonLoading = false
        showToastSuccess(this.$t('signatureChains.participants.resendSucces'))
      } catch (error) {
        console.error(error)
        showToastError(this.getErrorMessage(error))
      } finally {
        this.isResendButtonLoading = false
      }
    },
    getUserSignatureId(item) {
      const userSignature = item.signatures.find(signature => (!!signature.user && signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED))
      return userSignature.id
    },

    getCurrentSignatureId(item) {
      if (item.type === SignatureChainType.UNORDERED && !!item.currentSignatureId) {
        return item.currentSignatureId
      } else return ''
    },
    async copyItemSigningURL(item) {
      const signId = item.id
      const currentSignatureId = this.getCurrentSignatureId(this.signatureChain)
      // const currentSignatureId = item.id
      let docIdtoSign = this.signatureChain.origDocument.id

      const doc = this.signatureChain.signatures.findLast(x => x.signedDocument)
      if (doc) {
        docIdtoSign = doc.signedDocument.id
      }
      const isInClerkMode = this.signatureChain.signatures.find(signature => signature.id === signId).clerkMode
      const rdssPublicUrl = this.$store.state.remoteConfig && this.$store.state.remoteConfig.rdssPublicUrl
      if (this.signatureChain.signatures.find(signature => signature.id === signId).approver) {
        const approveUrl = rdssPublicUrl + 'document/' + (docIdtoSign) + '/approve-customization/' + signId + '/' + currentSignatureId
        this.copyURL(approveUrl)
        return
      }
      const url = isInClerkMode ? rdssPublicUrl + 'document/' + (docIdtoSign) + '/clerk/' + signId + '/' + currentSignatureId : rdssPublicUrl + 'document/' + this.signatureChain.id + '/' + (docIdtoSign) + '/customization/signature/' + signId + '/' + currentSignatureId
      this.copyURL(url)
    },
    async copyURL(url) {
      try {
        await navigator.clipboard.writeText(url)
      } catch ($e) {
      }
    }
  }
}
</script>

<style scoped>
.virtual-scroll-table {
  max-height: 20vh;
  overflow-y: auto;
  overflow-x: hidden !important;
}
</style>
